import { computed } from "vue"
import { useStore } from "vuex"
import { adminClient } from "@/api/clients/admin.client"
import errorHandler from "@/services/errorHandler"
import {
  DICT_LOAD_START,
  DICT_LOAD_COMPLETE,
  DICT_LOAD_FAIL,
} from "@/constants/events"
import logger from "@/services/logger"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_STORE_DICT } from "@/constants/topology"

// LLM:METADATA
// :topology: [Domain:Infrastructure | Role:Composable | Pattern:DataProvider]
// :relates-to: uses: "admin.client.js", uses: "errorHandler.js", required-by: "App.vue", "Login.vue"
// :rationale: "Centralizes dictionary loading to eliminate duplication and ensure caching. FE-GAP-006: Improved error handling and retry safety - globalIsLoading now reset in finally even on error, preventing blocking of retries."
// :contract: pre: "Store initialized", post: "Dictionaries loaded into Vuex store and accessible via computed properties", invariant: "Failed loads don't block future attempts"
// LLM:END

setTopologyId(TOPOLOGY_STORE_DICT)

// Global loading flag shared across all instances to prevent parallel calls
// FE-GAP-006: Reset in finally to ensure retries are possible after errors
let globalIsLoading = false

// Track which dicts have been successfully loaded for better error recovery
// FE-GAP-006: Track successful loads to prevent unnecessary retries
const loadedDicts = new Set()

export function useCommonDicts() {
  const store = useStore()
  // params is kept for future use when API URL needs to be dynamic
  const params = computed(() => store.getters.getParams())
  const dict = computed(() => store.getters.getDict())

  const loadDicts = async () => {
    // Prevent parallel calls using global flag
    // FE-GAP-006: This flag is now reset in finally block to allow retries after errors
    if (globalIsLoading) {
      logger.info(
        DICT_LOAD_START,
        "Dictionary load already in progress, skipping",
        {
          active: true,
        }
      )
      return
    }

    globalIsLoading = true

    logger.info(DICT_LOAD_START, "Loading dictionaries", {
      timestamp: Date.now(),
    })

    try {
      const loaders = [
        {
          key: "orderStatuses",
          method: adminClient.getOrderStatuses,
          action: "setOrderStatuses",
        },
        {
          key: "orderManagers",
          method: adminClient.getOrderManagers,
          action: "setOrderManagers",
        },
        {
          key: "paymentTypes",
          method: adminClient.getPaymentTypes,
          action: "setPaymentTypes",
        },
        {
          key: "deliveryTypes",
          method: adminClient.getDeliveryOptions,
          action: "setDeliveryTypes",
        },
      ]

      for (const { key, method, action } of loaders) {
        // Check if already loaded (either in store or tracked as loaded)
        if (loadedDicts.has(key) || dict?.value?.[key]?.length) {
          continue
        }

        try {
          const data = await method()
          store.dispatch(action, data)
          loadedDicts.add(key)
          logger.info(
            DICT_LOAD_COMPLETE,
            `Dictionary ${key} loaded successfully`,
            {
              itemCount: Array.isArray(data) ? data.length : 0,
            }
          )
        } catch (error) {
          // FE-GAP-006: Log error but continue with other dicts
          // Don't add to loadedDicts so retry is possible
          logger.error(DICT_LOAD_FAIL, `Failed to load dictionary ${key}`, {
            error: error.message,
            stack: error.stack,
          })
          errorHandler.handle(error)
          // Don't break the loop, continue with other dictionaries
        }
      }

      logger.info(DICT_LOAD_COMPLETE, "Dictionary loading complete", {
        loadedCount: loadedDicts.size,
        totalExpected: loaders.length,
      })
    } finally {
      // FE-GAP-006: Always reset flag to allow retries after errors
      // This fixes the issue where a failed load would block all future attempts
      globalIsLoading = false
    }
  }

  return { dict, loadDicts, params }
}
